import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RefreshIcon from "@mui/icons-material/Refresh";
import TuneIcon from "@mui/icons-material/Tune";
import { AppColors } from "../../constants/AppColors";
import { BookingResponse } from "../../models/ResponseModels/BookingResponse";
import { PropertyResponse } from "../../models/ResponseModels/PropertyResponse";
import { UnitResponse } from "../../models/ResponseModels/UnitResponse";
import {
  BookingColorMap,
  darkenHexColor,
  getBookingColor,
} from "../../utils/helperFunctions/bookingColorHelper";
import {
  formatCalendarDayName,
  formatCalendarMonthLabel,
  formatCalendarRange,
  getCalendarDayNumber,
  getCalendarMonthNumber,
  getCalendarMonthSegments,
  getCalendarUnitAccent,
  hexToRgba,
  isCalendarToday,
  isCalendarWeekend,
} from "../../utils/helperFunctions/calendarTimelineHelper";

interface MobileMultiCalendarViewProps {
  t: (key: string, values?: Record<string, string | number>) => string;
  dates: string[];
  properties: PropertyResponse[];
  units: UnitResponse[];
  apiStartDate: string;
  apiEndDate: string;
  openPropertyIds: number[];
  pendingBookingSelection: { unit: UnitResponse; date: string } | null;
  bookingColors: BookingColorMap;
  canManage: boolean;
  filtersOpen: boolean;
  getUnitsForProperty: (propertyId: number) => UnitResponse[];
  getBookingsForUnit: (unitId: number) => BookingResponse[];
  toggleProperty: (propertyId: number) => void;
  handleCalendarDateClick: (unit: UnitResponse, date: string) => void;
  openCreateBookingModal: (unit: UnitResponse) => void;
  openBookingDetails: (booking: BookingResponse) => void;
  previousRange: () => void;
  nextRange: () => void;
  refreshBookings: () => void;
  toggleFilters: () => void;
}

const MOBILE_CALENDAR_CELL_WIDTH = 74;
const MOBILE_CALENDAR_UNIT_COLUMN_WIDTH = 176;
const MOBILE_CALENDAR_ROW_HEIGHT = 74;

const normalizeDate = (value: string) => value.split("T")[0];

const getVisibleMobileBookingBarStyle = (
  booking: BookingResponse,
  dates: string[],
): { left: number; width: number } | null => {
  if (dates.length === 0) {
    return null;
  }

  const checkIn = normalizeDate(booking.check_in);
  const checkOut = normalizeDate(booking.check_out);
  const firstDate = dates[0];
  const lastDate = dates[dates.length - 1];

  if (checkOut < firstDate || checkIn > lastDate) {
    return null;
  }

  const visibleStart = checkIn < firstDate ? firstDate : checkIn;
  const visibleEnd = checkOut > lastDate ? lastDate : checkOut;
  const startIndex = dates.findIndex((date) => date === visibleStart);
  const endIndex = dates.findIndex((date) => date === visibleEnd);

  if (startIndex === -1 || endIndex === -1) {
    return null;
  }

  const startsBeforeVisibleRange = checkIn < firstDate;
  const endsAfterVisibleRange = checkOut > lastDate;

  const left =
    MOBILE_CALENDAR_UNIT_COLUMN_WIDTH +
    startIndex * MOBILE_CALENDAR_CELL_WIDTH +
    (startsBeforeVisibleRange ? 8 : MOBILE_CALENDAR_CELL_WIDTH / 2);

  const right =
    MOBILE_CALENDAR_UNIT_COLUMN_WIDTH +
    endIndex * MOBILE_CALENDAR_CELL_WIDTH +
    (endsAfterVisibleRange
      ? MOBILE_CALENDAR_CELL_WIDTH - 8
      : MOBILE_CALENDAR_CELL_WIDTH / 2);

  return {
    left,
    width: Math.max(right - left, 56),
  };
};

const MobileMultiCalendarView = ({
  t,
  dates,
  properties,
  units,
  apiStartDate,
  apiEndDate,
  openPropertyIds,
  pendingBookingSelection,
  bookingColors,
  canManage,
  filtersOpen,
  getUnitsForProperty,
  getBookingsForUnit,
  toggleProperty,
  handleCalendarDateClick,
  openCreateBookingModal,
  openBookingDetails,
  previousRange,
  nextRange,
  refreshBookings,
  toggleFilters,
}: MobileMultiCalendarViewProps) => {
  const monthSegments = getCalendarMonthSegments(dates);
  const minWidth =
    MOBILE_CALENDAR_UNIT_COLUMN_WIDTH +
    dates.length * MOBILE_CALENDAR_CELL_WIDTH;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 4,
        border: `1px solid ${AppColors.border}`,
        bgcolor: AppColors.card,
        overflow: "hidden",
        boxShadow: AppColors.shadowPanel,
      }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 1.25,
          borderBottom: `1px solid ${AppColors.borderSoft}`,
          bgcolor: AppColors.card,
        }}
      >
        <Stack spacing={1}>
          <Box>
            <Typography
              sx={{
                fontWeight: 950,
                fontSize: 15,
                lineHeight: 1.1,
                color: AppColors.textPrimary,
                textTransform: "uppercase",
              }}
            >
              {formatCalendarMonthLabel(apiStartDate)}
            </Typography>

            <Typography
              sx={{
                mt: 0.3,
                fontSize: 13,
                color: AppColors.textSecondary,
                fontWeight: 850,
              }}
            >
              {formatCalendarRange(apiStartDate, apiEndDate)}
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={0.75}
            sx={{
              overflowX: "auto",
              overflowY: "hidden",
              pb: 0.25,
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
            }}
          >
            <Button
              variant="outlined"
              onClick={previousRange}
              sx={{
                minWidth: 38,
                width: 38,
                height: 34,
                borderRadius: "12px",
                flexShrink: 0,
                px: 0,
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </Button>

            <Button
              variant="outlined"
              onClick={nextRange}
              sx={{
                minWidth: 38,
                width: 38,
                height: 34,
                borderRadius: "12px",
                flexShrink: 0,
                px: 0,
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </Button>

            <Button
              variant={filtersOpen ? "contained" : "outlined"}
              startIcon={<TuneIcon />}
              onClick={toggleFilters}
              sx={{
                height: 34,
                borderRadius: "999px",
                fontWeight: 900,
                flexShrink: 0,
                px: 1.4,
                "& .MuiButton-startIcon": {
                  mr: 0.75,
                },
              }}
            >
              {t("calendar.filters")}
            </Button>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={refreshBookings}
              sx={{
                height: 34,
                borderRadius: "999px",
                fontWeight: 900,
                flexShrink: 0,
                px: 1.4,
                "& .MuiButton-startIcon": {
                  mr: 0.75,
                },
              }}
            >
              {t("common.refresh")}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <CardContent sx={{ overflowX: "auto", p: 0 }}>
        <Box
          sx={{
            minWidth,
            bgcolor: AppColors.surface,
          }}
        >
          <MobileTimelineHeader
            t={t}
            dates={dates}
            units={units}
            monthSegments={monthSegments}
          />

          {properties.map((property) => {
            const propertyUnits = getUnitsForProperty(property.id);
            const isOpen = openPropertyIds.includes(property.id);

            return (
              <Box key={property.id}>
                <Box
                  onClick={() => toggleProperty(property.id)}
                  sx={{
                    minWidth,
                    px: 1.5,
                    py: 1.15,
                    bgcolor: AppColors.primary,
                    color: AppColors.textOnPrimary,
                    cursor: "pointer",
                    borderBottom: `1px solid ${AppColors.primary}`,
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 950 }}>
                    {isOpen ? "▾" : "▸"} {property.name}
                  </Typography>
                </Box>

                {isOpen && propertyUnits.length === 0 ? (
                  <Typography sx={{ p: 2 }} color="text.secondary">
                    {t("calendar.noUnits")}
                  </Typography>
                ) : null}

                {isOpen
                  ? propertyUnits.map((unit, index) => (
                      <MobileTimelineUnitRow
                        key={unit.id}
                        t={t}
                        unit={unit}
                        unitIndex={index}
                        dates={dates}
                        bookings={getBookingsForUnit(unit.id)}
                        pendingBookingSelection={pendingBookingSelection}
                        bookingColors={bookingColors}
                        canManage={canManage}
                        handleCalendarDateClick={handleCalendarDateClick}
                        openCreateBookingModal={openCreateBookingModal}
                        openBookingDetails={openBookingDetails}
                      />
                    ))
                  : null}
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

const MobileTimelineHeader = ({
  t,
  dates,
  units,
  monthSegments,
}: {
  t: (key: string, values?: Record<string, string | number>) => string;
  dates: string[];
  units: UnitResponse[];
  monthSegments: ReturnType<typeof getCalendarMonthSegments>;
}) => {
  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `${MOBILE_CALENDAR_UNIT_COLUMN_WIDTH}px repeat(${dates.length}, ${MOBILE_CALENDAR_CELL_WIDTH}px)`,
          borderBottom: `1px solid ${AppColors.borderSoft}`,
        }}
      >
        <Box
          sx={{
            position: "sticky",
            left: 0,
            zIndex: 8,
            bgcolor: AppColors.surface,
            borderRight: `1px solid ${AppColors.border}`,
          }}
        />

        {monthSegments.map((segment) => (
          <Box
            key={segment.key}
            sx={{
              gridColumn: `${segment.startIndex + 2} / span ${segment.span}`,
              px: 1,
              py: 1,
              bgcolor: AppColors.surface,
              color: AppColors.primary,
              fontSize: 11,
              fontWeight: 950,
              textAlign: "center",
              borderLeft: `1px solid ${AppColors.borderSoft}`,
            }}
          >
            {segment.label}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `${MOBILE_CALENDAR_UNIT_COLUMN_WIDTH}px repeat(${dates.length}, ${MOBILE_CALENDAR_CELL_WIDTH}px)`,
          borderBottom: `1px solid ${AppColors.border}`,
        }}
      >
        <Box
          sx={{
            position: "sticky",
            left: 0,
            zIndex: 8,
            bgcolor: AppColors.surface,
            borderRight: `1px solid ${AppColors.border}`,
            px: 1.5,
            py: 1.5,
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 950,
              color: AppColors.textMuted,
              textTransform: "uppercase",
              letterSpacing: 0.7,
            }}
          >
            {t("calendar.unit")}
          </Typography>

          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 800,
              color: AppColors.textSecondary,
            }}
          >
            {units.length} {t("calendar.shown")}
          </Typography>
        </Box>

        {dates.map((date) => (
          <Box
            key={date}
            sx={{
              minHeight: 58,
              p: 0.75,
              textAlign: "center",
              borderLeft: `1px solid ${AppColors.borderSoft}`,
              bgcolor: isCalendarToday(date)
                ? "rgba(20, 184, 166, 0.12)"
                : isCalendarWeekend(date)
                  ? "rgba(251, 146, 60, 0.08)"
                  : AppColors.surface,
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 950,
                color: isCalendarToday(date)
                  ? "#0f766e"
                  : isCalendarWeekend(date)
                    ? "#9a3412"
                    : AppColors.textSecondary,
              }}
            >
              {formatCalendarDayName(date)}
            </Typography>

            <Typography
              sx={{
                mt: 0.2,
                fontSize: 15,
                fontWeight: 950,
                color: AppColors.textPrimary,
              }}
            >
              {getCalendarDayNumber(date)}
            </Typography>

            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 800,
                color: AppColors.textMuted,
              }}
            >
              {getCalendarMonthNumber(date)}
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  );
};

const MobileTimelineUnitRow = ({
  t,
  unit,
  unitIndex,
  dates,
  bookings,
  pendingBookingSelection,
  bookingColors,
  canManage,
  handleCalendarDateClick,
  openCreateBookingModal,
  openBookingDetails,
}: {
  t: (key: string, values?: Record<string, string | number>) => string;
  unit: UnitResponse;
  unitIndex: number;
  dates: string[];
  bookings: BookingResponse[];
  pendingBookingSelection: { unit: UnitResponse; date: string } | null;
  bookingColors: BookingColorMap;
  canManage: boolean;
  handleCalendarDateClick: (unit: UnitResponse, date: string) => void;
  openCreateBookingModal: (unit: UnitResponse) => void;
  openBookingDetails: (booking: BookingResponse) => void;
}) => {
  const accent = getCalendarUnitAccent(unit, unitIndex);

  return (
    <Box
      sx={{
        position: "relative",
        display: "grid",
        gridTemplateColumns: `${MOBILE_CALENDAR_UNIT_COLUMN_WIDTH}px repeat(${dates.length}, ${MOBILE_CALENDAR_CELL_WIDTH}px)`,
        minHeight: MOBILE_CALENDAR_ROW_HEIGHT,
        borderBottom: `1px solid ${AppColors.borderSoft}`,
      }}
    >
      <Box
        onClick={() => {
          if (canManage) {
            openCreateBookingModal(unit);
          }
        }}
        sx={{
          position: "sticky",
          left: 0,
          zIndex: 6,
          bgcolor: AppColors.surface,
          borderRight: `1px solid ${AppColors.border}`,
          p: 1,
          cursor: canManage ? "pointer" : "default",
        }}
      >
        <Box
          sx={{
            height: "100%",
            borderRadius: "16px",
            border: `1px solid ${accent}`,
            bgcolor: hexToRgba(accent, 0.1),
            px: 1,
            py: 0.9,
            display: "flex",
            gap: 1,
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "999px",
              bgcolor: accent,
              flexShrink: 0,
            }}
          />

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 950,
                fontSize: 12.5,
                color: AppColors.textPrimary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {unit.name}
            </Typography>

            <Typography
              sx={{
                fontWeight: 750,
                fontSize: 10.5,
                color: AppColors.textSecondary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {unit.capacity} {t("properties.guests")}
            </Typography>
          </Box>
        </Box>
      </Box>

      {dates.map((date) => (
        <Box
          key={`${unit.id}-${date}`}
          onClick={() => handleCalendarDateClick(unit, date)}
          sx={{
            minHeight: MOBILE_CALENDAR_ROW_HEIGHT,
            borderLeft: `1px solid ${AppColors.borderSoft}`,
            bgcolor:
              pendingBookingSelection?.unit.id === unit.id &&
              pendingBookingSelection.date === date
                ? AppColors.primarySoft
                : isCalendarToday(date)
                  ? "rgba(20, 184, 166, 0.10)"
                  : isCalendarWeekend(date)
                    ? "rgba(251, 146, 60, 0.055)"
                    : AppColors.surface,
            cursor: canManage ? "pointer" : "default",
            "&:hover": {
              bgcolor: canManage ? AppColors.surfaceBlue : undefined,
            },
          }}
        />
      ))}

      {bookings.map((booking) => (
        <MobileTimelineBookingBar
          key={booking.id}
          t={t}
          booking={booking}
          dates={dates}
          bookingColors={bookingColors}
          openBookingDetails={openBookingDetails}
        />
      ))}
    </Box>
  );
};

const MobileTimelineBookingBar = ({
  t,
  booking,
  dates,
  bookingColors,
  openBookingDetails,
}: {
  t: (key: string, values?: Record<string, string | number>) => string;
  booking: BookingResponse;
  dates: string[];
  bookingColors: BookingColorMap;
  openBookingDetails: (booking: BookingResponse) => void;
}) => {
  if (booking.status === "cancelled") {
    return null;
  }

  const style = getVisibleMobileBookingBarStyle(booking, dates);
  const color = getBookingColor(booking, bookingColors);

  if (!style) {
    return null;
  }

  const source = booking.source || "manual";

  return (
    <Box
      onClick={(event) => {
        event.stopPropagation();
        openBookingDetails(booking);
      }}
      sx={{
        position: "absolute",
        top: 19,
        left: style.left,
        width: style.width,
        height: 36,
        borderRadius: "999px",
        bgcolor: hexToRgba(color, 0.12),
        border: `1px solid ${color}`,
        borderBottom: `3px solid ${darkenHexColor(color, 20)}`,
        color: AppColors.textPrimary,
        cursor: "pointer",
        px: 1.25,
        display: "flex",
        alignItems: "center",
        gap: 1,
        overflow: "hidden",
        boxShadow: "0 10px 22px rgba(15, 23, 42, 0.10)",
        zIndex: 4,
      }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: "999px",
          bgcolor: AppColors.card,
          color,
          border: `1px solid ${color}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 11,
          fontWeight: 950,
          flexShrink: 0,
        }}
      >
        ↗
      </Box>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontWeight: 950,
            fontSize: 12,
            lineHeight: 1.05,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {booking.guest?.full_name || booking.guest?.first_name || "Occupied"}
        </Typography>
      </Box>

      <Chip
        size="small"
        label={t(`bookingSources.${source}`).toUpperCase()}
        variant="outlined"
        sx={{
          height: 18,
          fontSize: 8.5,
          fontWeight: 950,
          borderRadius: "999px",
          flexShrink: 0,
          ...getSourceChipSx(source),
        }}
      />
    </Box>
  );
};

const getSourceChipSx = (source: string) => {
  if (source === "airbnb") {
    return {
      bgcolor: "#fee2e2",
      color: "#e11d48",
      borderColor: "#fecdd3",
    };
  }

  if (source === "booking") {
    return {
      bgcolor: "#dbeafe",
      color: "#2563eb",
      borderColor: "#bfdbfe",
    };
  }

  if (source === "manual") {
    return {
      bgcolor: "#f1f5f9",
      color: "#334155",
      borderColor: "#e2e8f0",
    };
  }

  return {
    bgcolor: "#ffedd5",
    color: "#f97316",
    borderColor: "#fed7aa",
  };
};

export default MobileMultiCalendarView;
